/**
 * File: apps/qbench-cp/reactapp/src/App/index.js
 *
 * Purpose: Root component that decides which top-level view to render based on auth and
 *     initialization state.
 *
 * Scope: Top-level app shell/routing decision for the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Reads appState from the app store to branch between a loading spinner, a network
 *     error view, the authenticated LoggedInApp (top nav, announcements, Dashboard, onboarding
 *     tour, division/field providers), and the sign-in page for unauthenticated users. On mount
 *     it triggers setPortalLogo to fetch the customer's portal logo. LoggedInApp itself wires
 *     the DashboardProvider and FieldProvider around the authenticated page content.
 *
 * Exports: App — default export, the root component mounted by the app entry point.
 */

import 'animate.css';
import { lazy, useEffect } from 'react';
import 'react-datetime/css/react-datetime.css';
import 'react-notifications/lib/notifications.css';
import Loading from 'shared/layout/Loading';
import PortalTopNav from '../shared/layout/PortalTopNav';
import Dashboard from '../Dashboard';
import { useAppStore } from './store';
import { StyledLoggedInApp } from './styles';
import { DashboardProvider } from 'Dashboard/state';
import { ThemeProvider } from 'styled-components';
import { useTheme } from 'shared/theme/useTheme';
import OnboardingTour from 'shared/components/Tours/Onboarding';
import AnnouncementList from 'shared/components/AnnouncementList';
import { FieldProvider } from 'context/fields';

const PortalSignInPage = lazy(() => import('../Login'));
const NetworkError = lazy(() => import('shared/layout/NetworkError'));

function LoggedInApp() {
	return (
		<StyledLoggedInApp>
			<DashboardProvider>
				<FieldProvider>
					<PortalTopNav />
					<AnnouncementList />
					<Dashboard />
					<OnboardingTour />
				</FieldProvider>
			</DashboardProvider>
		</StyledLoggedInApp>
	);
}

function App() {
	const { theme } = useTheme();
	const { appState, setPortalLogo } = useAppStore();

	useEffect(() => setPortalLogo(), []);

	if (appState.isLoading) return <Loading dim />;

	if (appState.isInitializing) {
		return <ThemeProvider theme={theme}><Loading dim /></ThemeProvider>;
	}

	return (
		<div className="d-flex w-100">
			{appState.isNetworkError && <NetworkError />}
			{!appState.isNetworkError && appState.isAuth && (
				<LoggedInApp />
			)}
			{!appState.isNetworkError && !appState.isAuth && (
				<PortalSignInPage />
			)}
		</div>
	);
}

export default App;
